import { useState } from "react";
import { useAuth } from "./AuthContext";
import { changePassword } from "./api";
import "./Profile.css";

interface ProfileProps {
  onBack: () => void;
  onExport?: () => void;
}

export default function Profile({ onBack }: ProfileProps) {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [backupPassword, setBackupPassword] = useState("");
  const [backupError, setBackupError] = useState("");
  const [backupLoading, setBackupLoading] = useState(false);
  const [showBackupModal, setShowBackupModal] = useState(false);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!currentPassword || !newPassword) {
      setError("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    if (newPassword.length < 4) {
      setError("New password must be at least 4 characters");
      return;
    }

    setLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      setSuccess("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      setError(err.message || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDatabase = () => {
    setShowBackupModal(true);
    setBackupPassword("");
    setBackupError("");
  };

  const handleBackupConfirm = async () => {
    if (!backupPassword) {
      setBackupError("Password required");
      return;
    }

    setBackupLoading(true);
    setBackupError("");

    try {
      const response = await fetch("/api/me/db-download", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: backupPassword }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Download failed");
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = `whiteboard-backup-${new Date().toISOString().split("T")[0]}.json`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      // Download the file
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setShowBackupModal(false);
      setBackupPassword("");
    } catch (err: any) {
      setBackupError(err.message || "Download failed");
    } finally {
      setBackupLoading(false);
    }
  };

  const handleExportAllPdfs = () => {
    window.location.href = "/export";
  };

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <h1>Profile</h1>
          <button onClick={onBack}>Back to App</button>
        </div>

        <div className="profile-sections">
          <div className="profile-section">
            <h2>Account Info</h2>
            <div className="profile-info">
              <div className="info-row">
                <span className="info-label">Username</span>
                <span className="info-value">{user?.username}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Account Type</span>
                <span className="info-value">
                  {user?.is_admin === 1 ? (
                    <span className="admin-badge">Admin</span>
                  ) : (
                    "Standard"
                  )}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Created</span>
                <span className="info-value">
                  {user?.created_at
                    ? new Date(user.created_at).toLocaleDateString()
                    : "-"}
                </span>
              </div>
            </div>
          </div>

          <div className="profile-section">
            <h2>Backup</h2>
            <p className="section-description">
              Download all your notes, folders, and settings as a JSON file.
            </p>
            <button className="download-btn" onClick={handleDownloadDatabase}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download Backup
            </button>
          </div>

          <div className="profile-section">
            <h2>Export</h2>
            <p className="section-description">
              Download all your notes as PDFs organized in folders.
            </p>
            <button className="download-btn" onClick={handleExportAllPdfs}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <path d="M12 18v-6" />
                <path d="M9 15l3 3 3-3" />
              </svg>
              Export All Notes as PDFs
            </button>
          </div>

          <div className="profile-section full-width">
            <h2>Change Password</h2>
            {error && <div className="profile-error">{error}</div>}
            {success && <div className="profile-success">{success}</div>}
            <form onSubmit={handleChangePassword} className="password-form">
              <div className="form-group">
                <label htmlFor="current-password">Current Password</label>
                <input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
              <div className="form-group">
                <label htmlFor="new-password">New Password</label>
                <input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirm-password">Confirm New Password</label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <button type="submit" disabled={loading}>
                {loading ? "Changing..." : "Change Password"}
              </button>
            </form>
          </div>
        </div>
      </div>

      {showBackupModal && (
        <div className="modal-overlay" onClick={() => setShowBackupModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Password</h3>
            <p>Enter your password to download the backup.</p>
            {backupError && <div className="profile-error">{backupError}</div>}
            <input
              type="password"
              value={backupPassword}
              onChange={(e) => setBackupPassword(e.target.value)}
              placeholder="Password"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleBackupConfirm()}
            />
            <div className="modal-buttons">
              <button onClick={() => setShowBackupModal(false)}>Cancel</button>
              <button onClick={handleBackupConfirm} disabled={backupLoading}>
                {backupLoading ? "Downloading..." : "Download"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
